import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SectionHeading from '../components/SectionHeading';
import TipsterLogo from '../components/TipsterLogo';
import { PlanChip } from '../components/Pricing';
import { useData } from '../context/DataContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { CONTACT_EMAIL } from '../data/content';
import { popularPlan } from '../lib/tips';
import { Mail, CreditCard, Wallet, Bitcoin, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAYMENTS = [
  { id: 'paypal', name: 'PayPal', icon: Wallet, color: 'bg-[#003087] text-white', href: 'https://www.paypal.com', external: true },
  { id: 'skrill', name: 'Skrill', icon: CreditCard, color: 'bg-[#862165] text-white', href: 'https://www.skrill.com', external: true },
  { id: 'card', name: 'Bank Card', icon: CreditCard, color: 'bg-slate-900 text-white', href: '/pay-guide/card', external: false },
  { id: 'neteller', name: 'Neteller', icon: Wallet, color: 'bg-[#00ac41] text-white', href: 'https://www.neteller.com', external: true },
  { id: 'crypto', name: 'Crypto (USDT TRC20 & BEP20)', icon: Bitcoin, color: 'bg-amber-500 text-white', href: '/pay-guide/crypto', external: false }
];

function PaymentTile({ method }) {
  const Icon = method.icon;
  const inner = (
    <>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${method.color} shadow-md`}><Icon className="w-7 h-7" strokeWidth={2} /></div>
      <div className="mt-3 text-sm font-semibold text-slate-800 text-center leading-tight max-w-[140px]">{method.name}</div>
    </>
  );
  const cls = 'select-none px-5 py-5 rounded-2xl border border-slate-200 bg-white flex flex-col items-center justify-center min-h-[130px] min-w-[130px] shadow-sm hover:shadow-lg hover:scale-[1.06] hover:border-blue-300 transform-gpu transition-all duration-300 ease-out cursor-pointer';
  if (method.external) return <a href={method.href} target="_blank" rel="noopener noreferrer" className={cls} title={`Visit ${method.name}`} data-testid={`payment-tile-${method.id}`}>{inner}</a>;
  return <Link to={method.href} className={cls} title={method.name} data-testid={`payment-tile-${method.id}`}>{inner}</Link>;
}

export default function SubscribePage() {
  const { tipster: tipsterParam, days: daysParam } = useParams();
  const { tipsters } = useData();
  const [tipsterId, setTipsterId] = useState(tipsterParam || '');
  const [days, setDays] = useState(daysParam ? Number(daysParam) : null);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!tipsters.length) return;
    const t = tipsters.find(x => x.id === tipsterId) || tipsters[0];
    if (t.id !== tipsterId) setTipsterId(t.id);
    if (!t.plans.some(p => p.days === days)) setDays(popularPlan(t).days);
  }, [tipsters, tipsterId, days]);

  const tipster = tipsters.find(t => t.id === tipsterId);
  const plan = tipster?.plans.find(p => p.days === days);
  if (!tipster || !plan) return (<><Header /><div className="min-h-[60vh]" /><Footer /></>);

  const choose = (id) => { setTipsterId(id); const t = tipsters.find(x => x.id === id); setDays(popularPlan(t).days); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.trim().length < 2) return toast({ title: 'Please enter your name', variant: 'destructive' });
    if (!EMAIL_RE.test(form.email)) return toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
    if (!form.message || form.message.trim().length < 10) return toast({ title: 'Message too short', description: 'Please write at least 10 characters.', variant: 'destructive' });
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(), email: form.email.trim(),
        subject: `${tipster.name} — ${plan.days} days (€${plan.price})`,
        plan: `${tipster.name} · ${plan.days} days · €${plan.price}`,
        message: form.message.trim()
      });
      toast({ title: 'Request sent', description: `Thanks ${form.name.split(' ')[0]} — we will reply within 12 hours with payment instructions.` });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Something went wrong. Please try again shortly.', variant: 'destructive' });
    } finally { setSending(false); }
  };

  return (
    <>
      <Header />
      <section className="bg-mist border-b border-line py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow="Subscribe" title="Choose a tipster and a duration. Everything else is already decided." lead="Select the tipster whose format suits you, pick how long you want to receive their selections, choose a payment method and send us your request — we reply with secure payment instructions." />
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1fr_380px] gap-8">
          <div className="space-y-8 min-w-0">
            <div>
              <div className="eyebrow text-slate-500 mb-4">1 · Tipster</div>
              <div className="grid md:grid-cols-3 gap-3" data-testid="subscribe-tipsters">
                {tipsters.map(t => (
                  <button type="button" key={t.id} onClick={() => choose(t.id)} className={`text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${t.id === tipsterId ? 'border-royal ring-2 ring-royal/30 bg-ice' : 'border-line bg-white hover:border-royal/50'}`} data-testid={`subscribe-tipster-${t.id}`}>
                    <TipsterLogo tipster={t} className="w-11 h-11" />
                    <div className="min-w-0"><div className="font-display font-bold text-navy truncate">{t.name}</div><div className="text-[11px] text-slate-500">{t.formatLabel}</div></div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="eyebrow text-slate-500 mb-4">2 · Duration</div>
              <div className="grid sm:grid-cols-3 gap-3 pt-2" data-testid="subscribe-plans">
                {tipster.plans.map(p => <PlanChip key={p.days} tipster={tipster} plan={p} asLink={false} selected={p.days === days} onSelect={setDays} />)}
              </div>
            </div>

            <div>
              <div className="eyebrow text-slate-500 mb-4">3 · Payment method</div>
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8">
                <div className="flex flex-wrap items-start justify-center gap-4 md:gap-5">{PAYMENTS.map(m => <PaymentTile key={m.id} method={m} />)}</div>
              </div>
            </div>

            <form onSubmit={submit} noValidate>
              <div className="eyebrow text-slate-500 mb-4">4 · Your request</div>
              <div className="bg-white border border-line rounded-3xl p-6 md:p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="s-name">Name *</Label><Input id="s-name" data-testid="subscribe-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1.5 h-12 rounded-xl" /></div>
                  <div><Label htmlFor="s-email">Email *</Label><Input id="s-email" data-testid="subscribe-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl" /></div>
                  <div className="sm:col-span-2"><Label htmlFor="s-msg">Message *</Label><Textarea id="s-msg" data-testid="subscribe-message" rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={`I would like ${tipster.name} for ${plan.days} days and prefer to pay by …`} className="mt-1.5 rounded-xl" /></div>
                </div>
                <Button type="submit" size="lg" disabled={sending} data-testid="subscribe-submit" className="mt-6 w-full bg-royal hover:bg-royal-dark text-white font-bold h-14 rounded-full text-base disabled:opacity-70 whitespace-normal">
                  {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : `Request ${tipster.name} · ${plan.days} days — €${plan.price}`}
                </Button>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Mail className="w-4 h-4" /> Prefer email? <a href={`mailto:${CONTACT_EMAIL}`} className="text-royal font-semibold hover:underline ml-1 break-all">{CONTACT_EMAIL}</a></div>
              </div>
            </form>
          </div>

          <aside className="lg:sticky lg:top-28 h-fit" data-testid="order-summary">
            <div className="bg-navy text-white rounded-3xl p-7 grain relative overflow-hidden">
              <div className="eyebrow text-signal">Your selection</div>
              <div className="mt-5 flex items-center gap-4 pb-5 border-b border-white/10">
                <TipsterLogo tipster={tipster} className="w-12 h-12" />
                <div className="flex-1"><div className="font-display text-xl font-bold" data-testid="order-tipster">{tipster.name}</div><div className="text-sm text-slate-300">{tipster.formatLabel} · {plan.days} days</div></div>
                <div className="font-mono text-3xl font-bold" data-testid="order-total">€{plan.price}</div>
              </div>
              <div className="mt-5 space-y-2.5 text-sm text-slate-300">
                <div className="flex justify-between"><span>Per day</span><span className="font-mono text-white">≈ €{(plan.price / plan.days).toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Days included</span><span className="font-mono text-white">{plan.days}</span></div>
                <div className="flex justify-between"><span>Selections per day</span><span className="font-mono text-white">{tipster.format === 'double' ? '2 (one Double)' : '1'}</span></div>
              </div>
              <div className="mt-5 flex items-start gap-2 text-[11px] text-slate-400 leading-relaxed"><ShieldCheck className="w-4 h-4 flex-shrink-0 text-signal" /> 18+. Betting involves risk. We never guarantee results — see the archive for the full record.</div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </>
  );
}
